import React, { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import useUXStore from '../api/ux/store';
import { useDataGeneration } from '../api/generate/store';

const FILENAME_CONFIG_KEY = 'cocree_filename_config';

type FilenameConfigResult = {
    filename: string;
    submissionType: number;
    submissionAttempt: string;
};

type FilenameConfigContentProps = {
    onConfirm: (result: FilenameConfigResult) => void;
    initialSubmissionType?: number;
    providerCode?: string;
    reportPeriod?: string;
};

const normalizeSubmissionType = (value: unknown): "0" | "1" => {
    if (value === 1 || value === "1") {
        return "1";
    }
    return "0";
};

const FilenameConfigContent: React.FC<FilenameConfigContentProps> = ({ onConfirm, initialSubmissionType = 0, providerCode: passedProviderCode, reportPeriod: passedReportPeriod }) => {
    const { generateCocreeFilename, generated } = useDataGeneration(state => state);
    const { addToast } = useUXStore(state => state);
    
    const providerCode = passedProviderCode || generated?.Header["-ProviderCode"] || '';
    const reportPeriodRaw = passedReportPeriod || generated?.Header["-FileReferenceDate"] || '';
    
    // Convert YYYYMMDD to YYYY-MM-DD format for date input
    const formatDateForInput = (dateStr: string): string => {
        if (!dateStr || dateStr.length !== 8) return '';
        return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
    };
    
    const reportPeriod = formatDateForInput(reportPeriodRaw);
    //console.log("Provider Code:", generated);
    //const [providerCode, setProviderCode] = useState('');
    //const [reportPeriod, setReportPeriod] = useState('');
    const [submissionAttempt, setSubmissionAttempt] = useState('00');
    const [fileCountPart, setFileCountPart] = useState('00');
    const [fileNumber, setFileNumber] = useState('01');
    const [previewFilename, setPreviewFilename] = useState('Enter values above to see filename preview');
    const [isFormValid, setIsFormValid] = useState(false);
    const [submissionTypeInput, setSubmissionTypeInput] = useState<string>(normalizeSubmissionType(initialSubmissionType));
    
    // Load saved settings from localStorage when component mounts
    useEffect(() => {
        try {
            const savedConfig = localStorage.getItem(FILENAME_CONFIG_KEY);
            if (savedConfig) {
                const config = JSON.parse(savedConfig);
                // Remove fields values (fileCountPart, fileNumber) from config and resave a clean version to local storage
                const { fileCountPart: _legacyCount, fileNumber: _legacyNumber, ...sanitizedConfig } = config;
                if (
                    typeof _legacyCount !== "undefined" ||
                    typeof _legacyNumber !== "undefined"
                ) {
                    localStorage.setItem(FILENAME_CONFIG_KEY, JSON.stringify(sanitizedConfig));
                }

                //setProviderCode(config.providerCode || '');
                //setReportPeriod(config.reportPeriod || '');
                setSubmissionAttempt(typeof config.submissionAttempt !== "undefined" ? String(config.submissionAttempt) : '');
                if (typeof config.submissionType !== "undefined") {
                    setSubmissionTypeInput(normalizeSubmissionType(config.submissionType));
                }
            }
        } catch (error) {
            console.error("Failed to load filename config from localStorage:", error);
        }
    }, []);

    useEffect(() => {
        setSubmissionTypeInput(normalizeSubmissionType(initialSubmissionType));
    }, [initialSubmissionType]);

    useEffect(() => {
        const valid = providerCode.trim() !== '' && reportPeriod.trim() !== '';
        setIsFormValid(valid);

        const displayProviderCode = providerCode || 'XXXXXXXXXX';
        const displayReportPeriod = reportPeriod ? reportPeriod.replace(/-/g, '') : 'YYYYMMDD';
        const displaySubmissionAttempt = submissionAttempt ? submissionAttempt.padStart(2, '0') : '00';
        const displayFileCountPart = fileCountPart.padStart(2, '0');
        const displayFileNumber = fileNumber.padStart(2, '0');
        
        const fileReference = displayReportPeriod + displaySubmissionAttempt + displayFileCountPart + displayFileNumber;
        const filename = displayProviderCode.padStart(10, '0') + '_CSDF_' + fileReference + '.zip.gpg';
        
        setPreviewFilename(filename);
    }, [providerCode, reportPeriod, submissionAttempt, fileCountPart, fileNumber]);

    const handleConfirm = useCallback(() => {
        // Validation
        if (!providerCode || !reportPeriod) {
            addToast("Invalid Input", "Provider Code and Report Period are required", null, "danger");
            return;
        }
        
        if (providerCode.length > 10) {
            addToast("Invalid Input", "Provider Code must be 10 digits or less", null, "danger");
            return;
        }
        
        // Format report period from YYYY-MM-DD to YYYYMMDD
        const reportPeriodFormatted = reportPeriod.replace(/-/g, '');

        const parsedSubmissionType = Number(submissionTypeInput);
        if (parsedSubmissionType !== 0 && parsedSubmissionType !== 1) {
            addToast("Invalid Input", "Submission Type must be 0 or 1", null, "danger");
            return;
        }
        
        // Save settings to localStorage for future use
        const configToSave = {
            providerCode,
            reportPeriod,
            submissionAttempt: submissionAttempt === '' ? undefined : Number(submissionAttempt),
            submissionType: parsedSubmissionType
        };
        
        try {
            localStorage.setItem(FILENAME_CONFIG_KEY, JSON.stringify(configToSave));
        } catch (error) {
            console.error("Failed to save filename config to localStorage:", error);
            // Continue with download even if save fails
        }
        
        // Generate filename
        const filename = generateCocreeFilename(
            providerCode,
            reportPeriodFormatted,
            submissionAttempt,
            fileCountPart,
            fileNumber
        );

        onConfirm({
            filename,
            submissionType: parsedSubmissionType,
            submissionAttempt: submissionAttempt.padStart(2, '0')
        });
    }, [
        addToast,
        fileCountPart,
        fileNumber,
        generateCocreeFilename,
        providerCode,
        reportPeriod,
        submissionAttempt,
        submissionTypeInput,
        onConfirm
    ]);

    // Expose handleConfirm and form validity to parent dialog
    useEffect(() => {
        (window as any).handleFilenameConfirm = handleConfirm;
        (window as any).isFilenameFormValid = isFormValid;
        return () => {
            delete (window as any).handleFilenameConfirm;
            delete (window as any).isFilenameFormValid;
        };
    }, [handleConfirm, isFormValid]);

    return (
        <div>
            {/* <div style={{ marginBottom: '15px' }}>
                <p><strong>Configure the encrypted file name components:</strong></p>
                <Card className="bg-light" style={{ padding: '12px', fontFamily: 'monospace' }}>
                    <div style={{ color: '#495057', fontSize: '14px' }}>Format:</div>
                    <div style={{ color: '#007bff', fontWeight: 'bold' }}>XXXXXXXXXX_CSDF_FileReference.zip.gpg</div>
                    <div style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px' }}>
                        Where FileReference = YYYYMMDDAABBCC
                    </div>
                </Card>
            </div> */}
            
            <Form>
                <Form.Group className="mb-3">
                    <Form.Label><strong>Provider Code: <span style={{ color: 'red' }}>*</span></strong></Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter 10-digit provider code"
                        disabled={!!passedProviderCode || generated?.Header["-ProviderCode"] !== undefined}
                        maxLength={10}
                        value={providerCode}
                        //onChange={(e) => setProviderCode(e.target.value)}
                        isInvalid={providerCode.trim() === ''}
                    />
                    <Form.Text className="text-muted">10-digit provider identification code (required)</Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-3">
                    <Form.Label><strong>Report Reference Period: <span style={{ color: 'red' }}>*</span></strong></Form.Label>
                    <Form.Control
                        type="date"
                        disabled={!!passedReportPeriod || reportPeriod !== undefined}
                        value={reportPeriod}
                        //onChange={(e) => setReportPeriod(e.target.value)}
                        isInvalid={reportPeriod.trim() === ''}
                    />
                    <Form.Text className="text-muted">Date for the report reference period (required)</Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label><strong>Submission Type:</strong></Form.Label>
                    <Form.Select
                        value={submissionTypeInput}
                        onChange={(e) => setSubmissionTypeInput(normalizeSubmissionType(e.target.value))}
                    >
                        <option value="0">0 - Initial submission</option>
                        <option value="1">1 - Correction or resubmission</option>
                    </Form.Select>
                </Form.Group>
                
                <Row className="mb-3">
                    <Col>
                        <Form.Label><strong>Submission Attempt (AA):</strong></Form.Label>
                        <Form.Control
                            type="text"
                            inputMode="numeric"
                            pattern="\\d*"
                            maxLength={2}
                            value={submissionAttempt}
                            onChange={(e) => {
                                const raw = String(e.target.value || '');
                                const digits = raw.replace(/\D/g, '');
                                if (digits === '') {
                                    setSubmissionAttempt('');
                                    return;
                                }
                                let num = parseInt(digits, 10);
                                if (!isFinite(num) || num < 0) num = 0;
                                if (num > 99) num = 99;
                                setSubmissionAttempt(String(num));
                            }}
                        />
                        <Form.Text className="text-muted">0-99</Form.Text>
                    </Col>
                    <Col>
                        <Form.Label><strong>File Count Part (BB):</strong></Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            max="59"
                            value={fileCountPart}
                            onChange={(e) => setFileCountPart(e.target.value)}
                        />
                        {/*<Form.Text className="text-muted">00-59</Form.Text>*/}
                    </Col>
                    <Col>
                        <Form.Label><strong>File Number (CC):</strong></Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            max="99"
                            value={fileNumber}
                            onChange={(e) => setFileNumber(e.target.value)}
                        />
                        {/*<Form.Text className="text-muted">01-99</Form.Text>*/}
                    </Col>
                </Row>
                
                <Alert variant="info">
                    <strong>Preview:</strong> <span style={{ fontFamily: 'monospace' }}>{previewFilename}</span>
                </Alert>
            </Form>
        </div>
    );
};

type FilenameDialogOptions = {
    initialSubmissionType?: number;
    providerCode?: string;
    reportPeriod?: string;
};

export const useFilenameConfigDialog = () => {
    const { refreshDialog } = useUXStore(state => state);

    const showFilenameDialog = (
        onConfirm: (result: FilenameConfigResult) => void,
        options?: FilenameDialogOptions
    ) => {
        // Create initial dialog
        refreshDialog({
            id: "filename-config",
            title: "Download as",
            content: (
                <FilenameConfigContent
                    onConfirm={onConfirm}
                    initialSubmissionType={options?.initialSubmissionType}
                    providerCode={options?.providerCode}
                    reportPeriod={options?.reportPeriod}
                />
            ),
            confirmContent: "Download File",
            confirmActive: true, // Start disabled
            visible: true,
            onConfirm: () => {
                // Only proceed if form is valid
                if ((window as any).isFilenameFormValid && (window as any).handleFilenameConfirm) {
                    (window as any).handleFilenameConfirm();
                }
            }
        }, true);

    };

    return { showFilenameDialog };
};

export default useFilenameConfigDialog;
